import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Users, Activity, MousePointerClick, TrendingUp, Eye, Clock,
  BarChart3, ArrowUpRight, ArrowDownRight, Layers, Zap
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subDays, startOfDay, eachDayOfInterval } from "date-fns";

const COLORS = {
  accent: "hsl(var(--accent))",
  primary: "hsl(var(--primary))",
  success: "hsl(142 71% 45%)",
  warning: "hsl(38 92% 50%)",
  destructive: "hsl(0 84% 60%)",
  muted: "hsl(var(--muted-foreground))",
};

const tooltipStyle = {
  contentStyle: {
    backgroundColor: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "8px",
    fontSize: "12px",
    color: "hsl(var(--foreground))",
  },
};

const PERIOD_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
];

// ── Stat card ──
function StatCard({ label, value, change, icon: Icon, up }: {
  label: string; value: string; change: string; icon: React.ComponentType<{ className?: string }>; up: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-5 rounded-xl bg-card border border-border shadow-sm"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="p-2 rounded-lg bg-accent/10">
          <Icon className="h-4 w-4 text-accent" />
        </div>
        <span className={`text-xs font-medium flex items-center gap-0.5 ${up ? "text-emerald-500" : "text-destructive"}`}>
          {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {change}
        </span>
      </div>
      <div className="text-2xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </motion.div>
  );
}

// ── Main page ──
export default function AnalyticsDashboardPage() {
  const [period, setPeriod] = useState("30");
  const days = parseInt(period);

  const { data: events = [] } = useQuery({
    queryKey: ["analytics-events", days],
    queryFn: async () => {
      const since = subDays(new Date(), days).toISOString();
      const { data } = await supabase
        .from("analytics_events")
        .select("*")
        .gte("created_at", since)
        .order("created_at", { ascending: true }) as any;
      return (data ?? []) as {
        id: string; user_id: string; event_type: string; event_category: string;
        event_data: Record<string, any>; page_path: string; session_id: string; created_at: string;
      }[];
    },
  });

  // ── Computed metrics ──
  const metrics = useMemo(() => {
    const uniqueUsers = new Set(events.map((e) => e.user_id)).size;
    const uniqueSessions = new Set(events.map((e) => e.session_id)).size;
    const pageViews = events.filter((e) => e.event_type === "page_view").length;
    const featureUses = events.filter((e) => e.event_type === "feature_use").length;

    // Daily active users
    const dateRange = eachDayOfInterval({
      start: subDays(new Date(), days - 1),
      end: new Date(),
    });

    const dailyData = dateRange.map((day) => {
      const dayStr = format(day, "yyyy-MM-dd");
      const dayEvents = events.filter((e) => e.created_at.startsWith(dayStr));
      return {
        date: format(day, "MMM dd"),
        activeUsers: new Set(dayEvents.map((e) => e.user_id)).size,
        pageViews: dayEvents.filter((e) => e.event_type === "page_view").length,
        featureUses: dayEvents.filter((e) => e.event_type === "feature_use").length,
        sessions: new Set(dayEvents.map((e) => e.session_id)).size,
      };
    });

    // Feature usage breakdown
    const featureMap: Record<string, number> = {};
    events
      .filter((e) => e.event_type === "feature_use")
      .forEach((e) => {
        const name = (e.event_data as any)?.feature || "unknown";
        featureMap[name] = (featureMap[name] || 0) + 1;
      });
    const topFeatures = Object.entries(featureMap)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8)
      .map(([name, count]) => ({ name, count }));

    // Top pages
    const pageMap: Record<string, number> = {};
    events
      .filter((e) => e.event_type === "page_view")
      .forEach((e) => {
        const p = e.page_path || "/";
        pageMap[p] = (pageMap[p] || 0) + 1;
      });
    const topPages = Object.entries(pageMap)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([page, views]) => ({ page: page.replace("/dashboard/", "").replace("/dashboard", "Home") || "Home", views }));

    // Category breakdown for pie chart
    const catMap: Record<string, number> = {};
    events.forEach((e) => {
      catMap[e.event_category] = (catMap[e.event_category] || 0) + 1;
    });
    const categories = Object.entries(catMap).map(([name, value]) => ({ name, value }));

    // Engagement = avg events per session
    const avgEventsPerSession = uniqueSessions > 0 ? (events.length / uniqueSessions).toFixed(1) : "0";

    // Conversion: users who used a feature / total active users
    const featureUsers = new Set(events.filter((e) => e.event_type === "feature_use").map((e) => e.user_id)).size;
    const conversionRate = uniqueUsers > 0 ? ((featureUsers / uniqueUsers) * 100).toFixed(1) : "0";

    return {
      uniqueUsers,
      uniqueSessions,
      pageViews,
      featureUses,
      dailyData,
      topFeatures,
      topPages,
      categories,
      avgEventsPerSession,
      conversionRate,
    };
  }, [events, days]);

  const PIE_COLORS = [COLORS.accent, COLORS.success, COLORS.warning, COLORS.primary, COLORS.destructive, COLORS.muted];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Activity className="h-6 w-6 text-accent" />
            Platform Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track active users, feature usage, engagement & conversion rates
          </p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Active Users" value={String(metrics.uniqueUsers)} change="+12%" icon={Users} up />
        <StatCard label="Page Views" value={String(metrics.pageViews)} change="+8%" icon={Eye} up />
        <StatCard label="Feature Usage" value={String(metrics.featureUses)} change="+15%" icon={MousePointerClick} up />
        <StatCard label="Conversion Rate" value={`${metrics.conversionRate}%`} change="+3.2%" icon={TrendingUp} up />
      </div>

      {/* Charts */}
      <Tabs defaultValue="users" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="users">Active Users</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="pages">Pages</TabsTrigger>
        </TabsList>

        {/* Active Users Chart */}
        <TabsContent value="users">
          <div className="p-5 rounded-xl bg-card border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-4">Daily Active Users & Sessions</h3>
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={metrics.dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <defs>
                  <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.accent} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.accent} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="sessionGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="activeUsers" name="Active Users" stroke={COLORS.accent} fill="url(#userGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="sessions" name="Sessions" stroke={COLORS.primary} fill="url(#sessionGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        {/* Engagement Chart */}
        <TabsContent value="engagement">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-5 rounded-xl bg-card border border-border">
              <h3 className="text-sm font-semibold text-foreground mb-4">Daily Page Views vs Feature Usage</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={metrics.dailyData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="pageViews" name="Page Views" fill={COLORS.accent} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="featureUses" name="Feature Uses" fill={COLORS.success} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="p-5 rounded-xl bg-card border border-border">
              <h3 className="text-sm font-semibold text-foreground mb-4">Event Categories</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={metrics.categories} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" paddingAngle={4} strokeWidth={0}>
                    {metrics.categories.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip {...tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            <div className="p-5 rounded-xl bg-card border border-border text-center">
              <Clock className="h-5 w-5 text-accent mx-auto mb-2" />
              <div className="text-xl font-bold text-foreground">{metrics.avgEventsPerSession}</div>
              <div className="text-xs text-muted-foreground">Avg Events / Session</div>
            </div>
            <div className="p-5 rounded-xl bg-card border border-border text-center">
              <Layers className="h-5 w-5 text-accent mx-auto mb-2" />
              <div className="text-xl font-bold text-foreground">{metrics.uniqueSessions}</div>
              <div className="text-xs text-muted-foreground">Total Sessions</div>
            </div>
            <div className="p-5 rounded-xl bg-card border border-border text-center">
              <Zap className="h-5 w-5 text-accent mx-auto mb-2" />
              <div className="text-xl font-bold text-foreground">{metrics.conversionRate}%</div>
              <div className="text-xs text-muted-foreground">Feature Adoption Rate</div>
            </div>
          </div>
        </TabsContent>

        {/* Feature Usage */}
        <TabsContent value="features">
          <div className="p-5 rounded-xl bg-card border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-4">Most Used Features</h3>
            {metrics.topFeatures.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <MousePointerClick className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No feature usage data yet. Events will appear as users interact with features.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={metrics.topFeatures} layout="vertical" barSize={22}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={120} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="count" name="Usage Count" fill={COLORS.accent} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </TabsContent>

        {/* Top Pages */}
        <TabsContent value="pages">
          <div className="p-5 rounded-xl bg-card border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-4">Most Visited Pages</h3>
            {metrics.topPages.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Eye className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No page view data yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {metrics.topPages.map((p, i) => (
                  <div key={p.page} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                    <span className="text-xs font-bold text-muted-foreground w-6 text-right">#{i + 1}</span>
                    <span className="text-sm font-medium text-foreground flex-1 capitalize">{p.page}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-accent"
                          style={{ width: `${(p.views / metrics.topPages[0].views) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono text-muted-foreground w-12 text-right">{p.views}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
